<template>
	<main class="blog-post-page">
		<template v-if="post">
			<section class="blog-post-header">
				<div class="blog-container">
					<div class="blog-post-back">
						<BackLink :to="{ name: 'resources' }" label="resources" />
					</div>
					<div class="blog-post-header-inner">
						<CategoryTag :category="post.category" />
						<h1 class="blog-post-title">{{ post.title }}</h1>
						<div class="blog-post-byline">
							<span class="blog-post-byline-item">
								<svg class="blog-post-byline-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
									<path d="M8 2v4M16 2v4M3 10h18" />
									<rect x="3" y="4" width="18" height="18" rx="2" />
								</svg>
								{{ formattedDate }}
							</span>
							<span class="blog-post-byline-item">
								<svg class="blog-post-byline-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
									<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
									<circle cx="12" cy="7" r="4" />
								</svg>
								{{ post.author }}
							</span>
						</div>
					</div>
				</div>
			</section>

			<div class="blog-post-hero">
				<div class="blog-container">
					<div class="blog-post-hero-frame">
						<VideoEmbed
							v-if="isVideo"
							:you-tube-id="post.youTubeId"
							:title="post.title"
							:thumbnail="post.image || ''"
							:duration="post.duration || ''"
						/>
						<BlogImage v-else :src="post.image" :alt="post.imageAlt" aspect="16 / 9" :priority="true" />
					</div>
				</div>
			</div>

			<article v-if="postContent.length > 0" class="blog-post-article">
				<div class="blog-container">
					<div class="blog-post-content">
						<template v-for="(block, index) in postContent" :key="index">
							<p v-if="block.type === 'paragraph'" class="blog-post-paragraph">{{ block.text }}</p>
							<h2 v-else-if="block.type === 'heading'" class="blog-post-content-heading">{{ block.text }}</h2>
							<ul v-else-if="block.type === 'list'" class="blog-post-content-list">
								<li v-for="(item, itemIndex) in block.items" :key="itemIndex">{{ item }}</li>
							</ul>
						</template>
					</div>
				</div>
			</article>

			<section class="blog-post-related">
				<div class="blog-container">
					<div class="blog-post-related-inner">
						<div class="blog-post-related-head">
							<h2 class="blog-post-related-title">Keep exploring</h2>
							<RouterLink :to="{ name: 'resources' }" class="blog-post-related-all">
								View all resources
								<svg class="blog-read-link-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
									<path d="M5 12h14M12 5l7 7-7 7" />
								</svg>
							</RouterLink>
						</div>
						<div class="blog-grid">
							<RouterLink
								v-for="related in relatedPosts"
								:key="related.slug"
								:to="{ name: 'resourcePost', params: { slug: related.slug } }"
								class="blog-card"
							>
								<div class="blog-card-media">
									<BlogImage :src="relatedImage(related)" :fallback-src="relatedImageFallback(related)" :alt="related.imageAlt" aspect="16 / 10" />
									<template v-if="related.type === 'video'">
										<span class="blog-media-play" aria-hidden="true">
											<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
										</span>
										<span v-if="related.duration" class="blog-media-duration" aria-hidden="true">{{ related.duration }}</span>
									</template>
								</div>
								<div class="blog-post-related-card-body">
									<CategoryTag :category="related.category" />
									<h3 class="blog-post-related-card-title">{{ related.title }}</h3>
									<span class="blog-read-link blog-post-related-read">
										{{ related.type === 'video' ? "Watch" : "Read more" }}
										<svg class="blog-read-link-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
											<path d="M5 12h14M12 5l7 7-7 7" />
										</svg>
									</span>
								</div>
							</RouterLink>
						</div>
					</div>
				</div>
			</section>
		</template>

		<section v-else class="blog-post-notfound">
			<div class="blog-container">
				<div class="blog-post-notfound-inner">
					<h1 class="blog-post-notfound-title">Page not found</h1>
					<p class="blog-post-notfound-text">The resource you are looking for does not exist or may have been moved.</p>
					<RouterLink :to="{ name: 'resources' }" class="blog-post-notfound-button">View all resources</RouterLink>
				</div>
			</div>
		</section>
	</main>
</template>

<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useSeoMeta, useHead } from "@unhead/vue";
import { resourceItems as posts } from "@/components/blog/resourceItems";
import { formatPostDate } from "@/components/blog/blogPosts";
import BackLink from "@/components/blog/BackLink.vue";
import BlogImage from "@/components/blog/BlogImage.vue";
import CategoryTag from "@/components/blog/CategoryTag.vue";
import VideoEmbed from "@/components/blog/VideoEmbed.vue";

const route = useRoute();

const post = computed(() => posts.find((entry) => entry.slug === route.params.slug) ?? null);

const isVideo = computed(() => post.value?.type === "video");

const postContent = computed(() => post.value?.content ?? []);

const relatedPosts = computed(() => posts.filter((entry) => entry.slug !== route.params.slug).slice(0, 3));

const formattedDate = computed(() => (post.value ? formatPostDate(post.value.date) : ""));

function relatedImage(related) {
	if (related.image) {
		return related.image;
	}
	return `https://i.ytimg.com/vi/${related.youTubeId}/maxresdefault.jpg`;
}

function relatedImageFallback(related) {
	return related.type === "video" ? `https://i.ytimg.com/vi/${related.youTubeId}/hqdefault.jpg` : "";
}

const postTitle = computed(() => post.value ? `${post.value.title} | Lucentary` : "Page Not Found | Lucentary");
const postDescription = computed(() => post.value ? post.value.excerpt : "The resource you are looking for does not exist.");
const postCanonical = computed(() => `https://lucentary.com/resources/${route.params.slug}`);
const postRobots = computed(() => post.value ? "index, follow" : "noindex, follow");
const postImage = computed(() => {
	if (!post.value) {
		return "https://lucentary.com/og-image.png";
	}
	if (post.value.image) {
		return post.value.image;
	}
	if (post.value.type === "video") {
		return `https://i.ytimg.com/vi/${post.value.youTubeId}/maxresdefault.jpg`;
	}
	return "https://lucentary.com/og-image.png";
});

useSeoMeta({
	title: postTitle,
	description: postDescription,
	ogTitle: postTitle,
	ogDescription: postDescription,
	ogUrl: postCanonical,
	ogImage: postImage,
	twitterTitle: postTitle,
	twitterDescription: postDescription,
	twitterImage: postImage,
	robots: postRobots,
});

useHead(() => ({
	link: [
		{ rel: "canonical", href: postCanonical.value },
	],
	script: post.value ? [{
		type: "application/ld+json",
		innerHTML: JSON.stringify(post.value.type === "video" ? {
			"@context": "https://schema.org",
			"@type": "VideoObject",
			name: post.value.title,
			description: post.value.excerpt,
			thumbnailUrl: postImage.value,
			uploadDate: post.value.date,
			embedUrl: `https://www.youtube-nocookie.com/embed/${post.value.youTubeId}`,
			contentUrl: `https://www.youtube.com/watch?v=${post.value.youTubeId}`,
			publisher: { "@type": "Organization", name: "Lucentary", url: "https://lucentary.com" },
		} : {
			"@context": "https://schema.org",
			"@type": "Article",
			headline: post.value.title,
			datePublished: post.value.date,
			author: { "@type": "Person", name: post.value.author },
			publisher: { "@type": "Organization", name: "Lucentary", url: "https://lucentary.com" },
			image: post.value.image,
			url: postCanonical.value,
		}),
	}] : [],
}));
</script>

<style scoped>
.blog-post-page {
	font-family: "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	color: #334155;
}

.blog-container {
	width: 100%;
	max-width: 80rem;
	margin: 0 auto;
	padding: 0 1rem;
}

.blog-post-header {
	background-color: #f8fafc;
	padding: 1.5rem 0 3rem;
}

.blog-post-back {
	margin-bottom: 2rem;
}

.blog-post-header-inner {
	max-width: 48rem;
	margin: 0 auto;
}

.blog-post-title {
	margin: 1rem 0 0;
	font-size: 1.875rem;
	font-weight: 800;
	line-height: 1.2;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-post-byline {
	margin-top: 1.5rem;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5rem 1.25rem;
	font-size: 0.875rem;
	color: #475569;
}

.blog-post-byline-item {
	display: inline-flex;
	align-items: center;
	gap: 0.375rem;
}

.blog-post-byline-icon {
	width: 1rem;
	height: 1rem;
}

.blog-post-hero {
	background-color: #ffffff;
}

.blog-post-hero-frame {
	max-width: 56rem;
	margin: -1rem auto 0;
	border-radius: 1rem;
	overflow: hidden;
	box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.05), 0 4px 6px -4px rgba(15, 23, 42, 0.05);
}

.blog-post-article {
	background-color: #ffffff;
	padding: 1.5rem 0 3rem;
}

.blog-post-content {
	max-width: 42rem;
	margin: 0 auto;
}

.blog-post-paragraph {
	margin: 1.25rem 0 0;
	font-size: 1rem;
	line-height: 1.625;
	color: #334155;
}

.blog-post-content-heading {
	margin: 2.5rem 0 0;
	font-size: 1.5rem;
	font-weight: 700;
	line-height: 1.3;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-post-content-list {
	margin: 1.25rem 0 0;
	padding-left: 1.5rem;
	list-style: disc;
	font-size: 1rem;
	line-height: 1.625;
	color: #334155;
}

.blog-post-content-list li:not(:first-child) {
	margin-top: 0.5rem;
}

.blog-post-related {
	background-color: #f8fafc;
	padding: 5rem 0;
}

.blog-post-related-inner {
	max-width: 72rem;
	margin: 0 auto;
}

.blog-post-related-head {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 1rem;
}

.blog-post-related-title {
	margin: 0;
	font-size: 1.5rem;
	font-weight: 800;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-post-related-all {
	display: none;
	align-items: center;
	gap: 0.375rem;
	font-size: 0.875rem;
	font-weight: 600;
	color: #1d4ed8;
	text-decoration: none;
	transition: color 0.18s ease;
}

.blog-post-related-all:hover {
	color: #1e40af;
}

.blog-grid {
	margin-top: 2rem;
	display: grid;
	gap: 1.5rem;
	grid-template-columns: 1fr;
}

.blog-card {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	border-radius: 1rem;
	border: 1px solid #e2e8f0;
	background-color: #ffffff;
	text-decoration: none;
	color: inherit;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.blog-card:hover {
	border-color: #93c5fd;
	box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.1), 0 2px 4px -2px rgba(15, 23, 42, 0.1);
}

.blog-card-media {
	position: relative;
}

.blog-media-play {
	position: absolute;
	top: 50%;
	left: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 3.25rem;
	height: 3.25rem;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.92);
	color: #2563eb;
	box-shadow: 0 10px 22px -10px rgba(15, 39, 66, 0.55);
	-webkit-transform: translate(-50%, -50%);
	transform: translate(-50%, -50%);
	transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
	z-index: 1;
}

.blog-media-play svg {
	width: 1.4rem;
	height: 1.4rem;
	margin-left: 0.12rem;
}

.blog-media-duration {
	position: absolute;
	right: 0.6rem;
	bottom: 0.6rem;
	padding: 0.15rem 0.45rem;
	border-radius: 0.35rem;
	background: rgba(15, 23, 42, 0.85);
	color: #ffffff;
	font-size: 0.72rem;
	font-weight: 600;
	z-index: 1;
}

.blog-post-related-card-body {
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 1.25rem;
}

.blog-post-related-card-title {
	margin: 0.75rem 0 0;
	font-size: 1rem;
	font-weight: 700;
	line-height: 1.375;
	color: #0f172a;
}

.blog-read-link {
	display: inline-flex;
	width: fit-content;
	align-items: center;
	gap: 0.375rem;
	font-size: 0.875rem;
	font-weight: 600;
	color: #1d4ed8;
	transition: color 0.18s ease;
}

.blog-post-related-read {
	margin-top: auto;
	padding-top: 0.75rem;
}

.blog-read-link-icon {
	width: 1rem;
	height: 1rem;
	-webkit-transition: -webkit-transform 0.18s ease;
	transition: transform 0.18s ease;
}

.blog-card:hover .blog-read-link {
	color: #1e40af;
}

.blog-card:hover .blog-read-link-icon {
	-webkit-transform: translateX(2px);
	transform: translateX(2px);
}

.blog-card:hover .blog-media-play {
	-webkit-transform: translate(-50%, -50%) scale(1.08);
	transform: translate(-50%, -50%) scale(1.08);
	background: #2563eb;
	color: #ffffff;
}

.blog-card:hover :deep(.blog-image-img) {
	-webkit-transform: scale(1.05);
	transform: scale(1.05);
}

.blog-post-notfound {
	background-color: #ffffff;
	padding: 6rem 0;
}

.blog-post-notfound-inner {
	max-width: 36rem;
	margin: 0 auto;
	text-align: center;
}

.blog-post-notfound-title {
	margin: 0;
	font-size: 1.875rem;
	font-weight: 800;
	letter-spacing: -0.025em;
	color: #0f172a;
}

.blog-post-notfound-text {
	margin: 1rem 0 0;
	font-size: 1rem;
	color: #475569;
}

.blog-post-notfound-button {
	margin-top: 2rem;
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	border-radius: 0.5rem;
	padding: 0.75rem 1.5rem;
	font-size: 1rem;
	font-weight: 600;
	background-color: #2563eb;
	color: #ffffff;
	text-decoration: none;
	box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.1), 0 2px 4px -2px rgba(15, 23, 42, 0.1);
	-webkit-transition: box-shadow 0.2s ease, -webkit-filter 0.2s ease;
	transition: box-shadow 0.2s ease, filter 0.2s ease;
}

.blog-post-notfound-button:hover {
	box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.12), 0 4px 6px -4px rgba(15, 23, 42, 0.12);
	-webkit-filter: brightness(0.95);
	filter: brightness(0.95);
}

@media (min-width: 640px) {
	.blog-container {
		padding: 0 1.5rem;
	}

	.blog-post-header {
		padding: 2rem 0 4rem;
	}

	.blog-post-title {
		font-size: 2.25rem;
	}

	.blog-post-hero-frame {
		margin-top: -1.5rem;
	}

	.blog-post-article {
		padding: 2rem 0 4rem;
	}

	.blog-post-paragraph {
		font-size: 1.125rem;
		line-height: 1.7;
	}

	.blog-post-content-heading {
		font-size: 1.875rem;
	}

	.blog-post-content-list {
		font-size: 1.125rem;
	}

	.blog-post-related {
		padding: 6rem 0;
	}

	.blog-post-related-title {
		font-size: 1.875rem;
	}

	.blog-post-related-all {
		display: inline-flex;
	}

	.blog-grid {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}

	.blog-post-related-card-title {
		font-size: 1.125rem;
	}

	.blog-post-notfound {
		padding: 8rem 0;
	}

	.blog-post-notfound-title {
		font-size: 2.25rem;
	}

	.blog-post-notfound-text {
		font-size: 1.125rem;
	}
}

@media (min-width: 1024px) {
	.blog-container {
		padding: 0 2rem;
	}

	.blog-post-title {
		font-size: 3rem;
	}

	.blog-grid {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
}
</style>